import React from "react";
import {
    Badge,
    BadgeColor,
    createListTableColumns,
    ListTableColumn,
    renderDateTime,
    renderOptionalTextWithTooltip,
    renderTextWithTooltip,
    TableColumn
} from "sprint-common-web";

import { NoteItem } from "../../../../common/components/notes/hooks/state/NoteItem";
import { renderTrailerInfo } from "../../../../common/components/trailer-info/renderTrailerInfo";
import { NoteSubjectType } from "../../../../common/types/NoteSubjectType";
import { renderNotes } from "../../../../common/utils/renderNotes";
import { getCiFileRecordQcReviewPageHref } from "../../../ContentIngestionRoutes";
import { renderContent } from "../../common/components/renderContent";
import { renderFileRecordDetails } from "../../common/components/renderFileRecordDetails";
import { renderFileRecordStatus } from "../../common/utils/renderFileRecordStatus";
import { renderFileRecordStatusDetails } from "../../common/utils/renderFileRecordStatusDetails";
import { renderFileRecordType } from "../../common/utils/renderFileRecordType";
import { FileRecordActionsListingButton } from "../actions/FileRecordActionsListingButtons";
import {
    FileRecordColumn,
    FileRecordListingConfig,
    sortColumnsByConfiguredOrder
} from "../config/FileRecordListingConfig";
import { renderIsNotificatonApproved } from "../FileRecordListing";
import { FileRecordListingItem } from "../types/FileRecordListingItem";

type DetailedFileRecordItem = FileRecordListingItem & { details: JSX.Element; link: string };

type FileRecordListingColumn = TableColumn<DetailedFileRecordItem>;

export type CreateFileRecordListingColumnsProps = {
    config: FileRecordListingConfig;
    notes?: Record<string, NoteItem[]>;
    areNotesLoading: boolean;
    shortNotePreview?: boolean;
    openInNewTab?: boolean;
    linkAsSeparateColumn?: boolean;
};

type GetListingColumnsProps = CreateFileRecordListingColumnsProps;

export const createFileRecordListingColumns = ({
    config,
    notes,
    areNotesLoading,
    shortNotePreview = false,
    linkAsSeparateColumn = false
}: CreateFileRecordListingColumnsProps): ListTableColumn<FileRecordListingItem>[] => {
    const columns = getListingColumns({ config, areNotesLoading, notes, shortNotePreview, linkAsSeparateColumn });

    return createListTableColumns<DetailedFileRecordItem>(columns);
};

const getListingColumns = ({
    config,
    notes,
    areNotesLoading,
    shortNotePreview,
    openInNewTab,
    linkAsSeparateColumn
}: GetListingColumnsProps): FileRecordListingColumn[] => {
    const mapping: Record<FileRecordColumn, FileRecordListingColumn | undefined> = {
        [FileRecordColumn.DeliveryFileName]: getFileRecordDeliveryFileNameColumn({ linkAsSeparateColumn }),
        [FileRecordColumn.Link]: getFileRecordLinkColumn({ linkAsSeparateColumn, openInNewTab }),
        [FileRecordColumn.FilePath]: {
            type: "basic",
            key: "filePath",
            name: "File path",
            minWidth: 80,
            maxWidth: 200,
            isSortable: true,
            onRender: renderOptionalTextWithTooltip
        },
        [FileRecordColumn.Content]: {
            type: "basic",
            key: "content",
            name: "Content",
            minWidth: 65,
            maxWidth: 350,
            isSortable: true,
            onRender: renderContent
        },
        [FileRecordColumn.RecordType]: {
            type: "basic",
            key: "recordType",
            name: "Record type",
            minWidth: 80,
            maxWidth: 80,
            onRender: renderFileRecordType
        },
        [FileRecordColumn.Info]: {
            type: "basic",
            key: "isTrailer",
            name: "Info",
            minWidth: 60,
            maxWidth: 80,
            onRender: renderTrailerInfo
        },
        [FileRecordColumn.Reference]: {
            type: "basic",
            key: "deliveryRef",
            name: "Reference",
            minWidth: 65,
            maxWidth: 80,
            isSortable: true
        },
        [FileRecordColumn.Details]: {
            type: "basic",
            key: "details",
            name: "Details",
            minWidth: 80,
            maxWidth: 120,
            isSortable: false,
            onRender: renderFileRecordDetails
        },
        [FileRecordColumn.BuzzNotifications]: {
            type: "basic",
            key: "isNotificationApproved",
            name: "Buzz notifications",
            minWidth: 80,
            maxWidth: 120,
            isSortable: false,
            onRender: renderIsNotificatonApproved
        },
        [FileRecordColumn.Status]: {
            type: "basic",
            key: "status",
            name: "Status",
            minWidth: 80,
            maxWidth: 80,
            isSortable: false,
            onRender: renderFileRecordStatus
        },
        [FileRecordColumn.StatusDetails]: {
            type: "basic",
            key: "statusDetails",
            name: "Status details",
            minWidth: 100,
            maxWidth: 250,
            isSortable: false,
            onRender: renderFileRecordStatusDetails
        },
        [FileRecordColumn.CreateDate]: {
            type: "basic",
            key: "createdDate",
            name: "Create date",
            minWidth: 150,
            maxWidth: 150,
            isResizable: false,
            isSortable: true,
            onRender: renderDateTime
        },
        [FileRecordColumn.UpdateDate]: {
            type: "basic",
            key: "updatedDate",
            name: "Update date",
            minWidth: 150,
            maxWidth: 150,
            isResizable: false,
            isSortable: true,
            onRender: renderDateTime
        },
        [FileRecordColumn.Notes]: {
            type: "basic",
            key: "notes",
            name: "Notes",
            minWidth: 120,
            maxWidth: 120,
            isResizable: false,
            onRender: (item) =>
                renderNotes({
                    itemId: item?.id,
                    notes: notes && item?.id ? notes[item.id] : undefined,
                    isLoading: areNotesLoading,
                    shortPreview: shortNotePreview,
                    subjectType: NoteSubjectType.FileRecord,
                    renderBadge: () => {
                        return (
                            <Badge
                                text="On-Prem"
                                color={BadgeColor.Blue}
                                size={12}
                                testId="note-on-prem-badge"
                                tooltipContent="File record note"
                            />
                        );
                    }
                })
        },
        [FileRecordColumn.Actions]: {
            type: "buttons",
            key: "actions",
            name: "Actions",
            minWidth: 60,
            maxWidth: 60,
            isResizable: true,
            buttons: [
                {
                    id: "actions",
                    render: FileRecordActionsListingButton
                }
            ]
        }
    };

    return sortColumnsByConfiguredOrder(config.columns)
        .filter(([_, column]) => column.visible)
        .map(([key]) => mapping[Number(key) as FileRecordColumn])
        .filter((column): column is FileRecordListingColumn => column !== undefined);
};

const getFileRecordDeliveryFileNameColumn = ({
    linkAsSeparateColumn,
    openInNewTab
}: {
    linkAsSeparateColumn?: boolean;
    openInNewTab?: boolean;
}): FileRecordListingColumn =>
    linkAsSeparateColumn
        ? {
              type: "basic",
              key: "deliveryFileName",
              name: "Delivery file name",
              minWidth: 120,
              maxWidth: 240,
              isSortable: true,
              onRender: renderTextWithTooltip
          }
        : {
              type: "link",
              key: "deliveryFileName",
              name: "Delivery file name",
              minWidth: 120,
              maxWidth: 240,
              isSortable: true,
              openInNewTab: () => openInNewTab ?? false,
              getHref: (item: FileRecordListingItem) => getCiFileRecordQcReviewPageHref(item.id),
              getTooltipText: (item) => item.deliveryFileName
          };

const getFileRecordLinkColumn = ({
    linkAsSeparateColumn,
    openInNewTab
}: {
    linkAsSeparateColumn?: boolean;
    openInNewTab?: boolean;
}): FileRecordListingColumn | undefined =>
    linkAsSeparateColumn
        ? {
              type: "link",
              key: "link",
              name: "Link",
              minWidth: 30,
              maxWidth: 30,
              isSortable: false,
              openInNewTab: () => openInNewTab ?? false,
              getHref: (item: FileRecordListingItem) => getCiFileRecordQcReviewPageHref(item.id)
          }
        : undefined;
